import { FC } from "react";
import { Box, styled, useTheme } from "@mui/system";
import muiIcon from "../../../components/icons/mui-icons";
import Containers from "../../../components/Containers";
import GullTable from "../../../components/tables/GullTable";
import { tableDataList } from "../../../utils/constant-data";
import { H2, H3, Paragraph, Span } from "../../../components/Typography";
import { CardContainer } from "../../../components/CardContainer";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import { GullButton } from "../../../components/buttons/GullButton";
import { sideTitleCardData, topAuthorList } from "./dashboard2-data";
import TwoColumnTable from "../../../components/tables/TwoColumnTable";
import BasicLineChart1 from "../../../components/apexCharts/BasicLineChart1";
import BasicDonutChart2 from "../../../components/apexCharts/BasicDonutChart2";
import {
  Avatar,
  Grid,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

const TableCardHeader = styled(H3)(({ theme }) => ({
  padding: ".8rem 1rem",
  background: theme.palette.mode === "light" && theme.palette.grey[50],
  borderRadius: "10px 10px 0px 0px",
}));

const StyledBox = styled(Box)(() => ({
  margin: "auto",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
}));

const IconBox = styled(Box)(() => ({
  height: "48px",
  width: "48px",
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  background: "#6b339921",
  justifyContent: "center",
}));

const ProductBox = styled(Box)(({ theme }) => ({
  marginTop: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "& p": {
    fontSize: 13,
    color: theme.palette.grey[600],
  },
}));
const CardBody = styled(Box)(() => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const CardImg = styled("img")(() => ({
  width: "55px",
  height: "55px",
  marginRight: 16,
  borderRadius: 5,
}));

const Status = styled(Span)<{ status: string }>(({ theme, status }) => ({
  fontSize: 11,
  borderRadius: 5,
  padding: "1px 5px",
  color:
    status === "active"
      ? theme.palette.grey[100]
      : status === "pending" && theme.palette.grey[100],
  background:
    status === "active"
      ? theme.palette.success.main
      : status === "pending"
        ? theme.palette.primary.main
        : status === "inactive" && theme.palette.warning.main,
}));

const Dashboard2: FC = () => {
  const theme = useTheme();
  const { CloseIcon, EditIcon } = muiIcon;
  const { ChartContainer, TableContainer } = Containers();

  return (
    <div>
      <Breadcrumb name="Dashboard" subName="Version 2" path="/dashboard/v1" />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            {sideTitleCardData.map((data) => {
              const { Icon, title, subtitle } = data;
              return (
                <Grid item xs={12} sm={4} md={4}>
                  <CardContainer
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <IconBox>
                      <Icon
                        sx={{
                          fontSize: "1.2rem",
                          color: "primary.main",
                        }}
                      />
                    </IconBox>
                    <StyledBox>
                      <Paragraph my="5px" fontSize=".8rem">
                        {subtitle}
                      </Paragraph>
                      <H2 fontSize="1.25rem" color="primary.main">
                        {title}
                      </H2>
                    </StyledBox>
                  </CardContainer>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ pb: 0 }}>
            <H3>Sales</H3>
            <BasicLineChart1
              data={[40, 80, 20, 90, 30, 80, 40]}
              color={theme.palette.primary.main}
              height={265}
            />
          </ChartContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12} md={6}>
          <ChartContainer>
            <H3 mb="1.5rem">Last Month Summary</H3>
            <Grid container>
              <Grid item xs={12} md={6}>
                <TwoColumnTable />
              </Grid>
              <Grid item xs={12} md={6}>
                <BasicDonutChart2 height={220} />
              </Grid>
            </Grid>
          </ChartContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <CardContainer>
            <H3 mb="-10px">Top Authors</H3>
            {topAuthorList.map((data, index) => {
              const { name, description, photoUrl } = data;
              return (
                <ProductBox key={index}>
                  <CardImg src={photoUrl} alt="" />
                  <CardBody sx={{ display: "flex" }}>
                    <Box>
                      <Paragraph color="#212121">{name}</Paragraph>
                      <Paragraph>{description}</Paragraph>
                    </Box>
                    <GullButton
                      rounded={true}
                      variant="outlined"
                      color="primary"
                      sx={{ p: ".1rem 1rem" }}
                    >
                      Follow
                    </GullButton>
                  </CardBody>
                </ProductBox>
              );
            })}
          </CardContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12} md={6}>
          <TableContainer sx={{ p: 0, pb: 2 }}>
            <TableCardHeader>New Users</TableCardHeader>
            <GullTable>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow hover id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <TableContainer sx={{ p: 0, pb: 2 }}>
            <TableCardHeader>Total Sales</TableCardHeader>
            <GullTable>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow hover id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </TableContainer>
        </Grid>
      </Grid>
    </div>
  );
};

export default Dashboard2;
